import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import AUHeader from '../components/AUHeader';
import VideoAccessForm from '../components/VideoAccessForm';
import DescargaPDF from '../components/DescargaPDF';
import { hasValidVideoAccess, setStoredVideoAccess } from '../utils/videoAccessStorage';
import { submitLead } from '../utils/submitLead';

const BLUE = '#1A7FFF';
const BLUE_HOVER = '#1568D9';
const BLUE_SOFT = '#EBF3FF';
const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const GRAY_LIGHT = '#F8FAFC';

const PDF_URL = '/recursos/guia-anti-atracones-au-coaching.pdf';

// ---- contenido de la guía ----
const JERARQUIA = [
  { n: '1', name: 'Adherencia', desc: 'Lo que realmente sostienes en el tiempo.', destacado: true },
  { n: '2', name: 'Calorías', desc: 'Balance energético total.' },
  { n: '3', name: 'Macros', desc: 'Distribución entre proteína, carbos y grasa.' },
  { n: '4', name: 'Micros', desc: 'Vitaminas, minerales, fibra.' },
  { n: '5', name: 'Timing', desc: 'Cuándo comes qué.' },
  { n: '6', name: 'Suplementos', desc: 'El último 1 a 2% del resultado.' },
];

const ESTRATEGIAS = [
  {
    n: '1', cat: 'Nutrición', title: 'Más proteína en cada comida',
    body: 'La proteína es el macro más saciante. Cuando cada comida tiene una buena dosis, el hambre entre comidas cae sola. Asegúrate de que esté presente siempre, en cantidad suficiente.',
    key: '2 a 3 g por kilo de peso al día. Distribuir en 4 a 5 comidas con 30 a 40 g cada una.',
    how: 'Pollo, huevos, carne, pescado, yogur de proteína o whey. Si una comida no tiene proteína visible, no está lista.',
  },
  {
    n: '2', cat: 'Hidratación', title: 'A veces no es hambre, es sed',
    body: 'La deshidratación imita la señal de hambre. Antes de buscar comida fuera de tus horarios, toma agua. Especialmente en la mañana, cuando llevas horas sin hidratarte y el cuerpo lo confunde con hambre real.',
    key: '30 a 40 ml de agua por kilo de peso al día. Para 80 kg, alrededor de 3 L.',
    how: 'Al despertar, un vaso de agua antes de cualquier otra cosa. Si tomas café de inmediato, agrega el agua primero.',
  },
  {
    n: '3', cat: 'Composición del plato', title: 'Densidad calórica baja',
    body: 'Puedes comer volumen alto con bajo costo calórico. Verduras, frutas y proteína magra te llenan el plato y el estómago sin comprometer el déficit. La restricción extrema es una de las causas directas del atracón.',
    key: '300 a 500 g de vegetales al día. Apunta a 25 a 38 g de fibra diaria.',
    how: 'La mitad del plato: verduras. Un cuarto: proteína magra. El cuarto restante: carbohidratos o grasa según tu fase.',
  },
  {
    n: '4', cat: 'Planificación', title: 'Come cuando más te cuesta resistir',
    body: 'No distribuyas las comidas de forma ideal en papel. Distribúyelas según tus momentos de mayor vulnerabilidad. Si en la noche te cuesta más, ahí tiene que estar tu comida más voluminosa o tu postre planificado.',
    key: 'Antes de dormir, reserva proteína, carbohidratos y grasa para esa comida. Es la hora donde más fácil se falla.',
    how: 'Si te quedan calorías disponibles para tu última comida, no fallas. La regla: nunca llegues a tu hora crítica con el plato vacío.',
  },
  {
    n: '5', cat: 'Comportamiento', title: 'Movimiento después de comer',
    body: '15 minutos de caminata después de comer. No es para quemar calorías. Es para cortar el ciclo de quedarte quieto con el foco en la comida. El movimiento cambia el estado mental y rompe la inercia del hambre emocional.',
    key: '10.000 a 12.000 pasos diarios. Si haces más, mejor.',
    how: 'Pararte de la mesa después de comer es lo más importante. Aunque sean 10 minutos de caminata, sin teléfono si puedes.',
  },
  {
    n: '6', cat: 'Entorno mental', title: 'Mantenerse ocupado',
    body: 'El atracón casi siempre ocurre cuando estás aburrido, estresado o solo con tu cabeza. Si tienes algo concreto que hacer, el espacio mental que genera el antojo simplemente no aparece. No es represión, es redirección.',
    key: 'El estrés sube ADH y cortisol, dos disparadores directos del antojo y de la retención de líquidos.',
    how: 'Ten una actividad de transición lista para tus momentos vulnerables: leer, trabajar, salir, llamar a alguien.',
  },
  {
    n: '7', cat: 'Diseño de entorno', title: 'Si no está en tu casa, no lo consumes',
    body: 'Esta es la estrategia más efectiva y la más ignorada. No se trata de tentar la voluntad. Se trata de diseñar el entorno para que caer sea difícil. Cuanta más fricción hay entre tú y el alimento que dispara el atracón, menos veces ocurre.',
    key: 'Los sistemas le ganan a la fuerza de voluntad. La voluntad se agota; el entorno trabaja 24/7 sin esfuerzo.',
    how: 'Regla simple: no compres lo que sabes que no puedes controlar en casa. No es fuerza de voluntad. Es no ponerla a prueba sin necesidad.',
    destacada: true,
  },
  {
    n: '8', cat: 'Mentalidad', title: 'Flexibilidad calculada: nada está prohibido',
    body: 'La mentalidad de todo o nada es la causa directa del atracón de 1.000 calorías extra. Cuando comes algo fuera del plan, no tires todo abajo. Solo comiste algo. Saber que todo se puede comer, dependiendo de la fase y de las calorías, es lo que separa al que avanza del que no.',
    key: 'La dieta flexible permite ±100 kcal del target diario y ±10 g por macro sin romper el plan.',
    how: 'Si comes algo que no estaba en el plan: calcula, ajusta el resto del día y sigue. Una comida no rompe el proceso. El atracón de "ya me mandé, total" sí lo rompe.',
  },
];

// ---- subcomponentes ----
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontFamily: 'Kanit, sans-serif', fontWeight: 800, color: DARK,
      fontSize: { xs: '1.3rem', md: '1.6rem' }, mb: 2.5, mt: 1,
    }}>
      {children}
    </Typography>
  );
}

function EstrategiaBlock({ e }) {
  return (
    <Paper elevation={0} sx={{
      borderRadius: 3, overflow: 'hidden', mb: 2.5,
      border: e.destacada ? `2px solid ${BLUE}` : `1px solid ${BORDER}`,
    }}>
      <Box sx={{
        bgcolor: BLUE_HOVER, px: { xs: 2, md: 2.5 }, py: 1.25,
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5,
      }}>
        <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: 16 }}>
          {e.n} · {e.title}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#CCE5FF', fontFamily: 'Kanit, sans-serif' }}>
          {e.cat}
        </Typography>
      </Box>
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
        <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 1.75 }}>
          {e.body}
        </Typography>
        <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, bgcolor: BLUE_SOFT, mb: 1.5 }}>
          <Typography sx={{
            fontWeight: 700, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontSize: 12,
            textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.5,
          }}>
            Número clave
          </Typography>
          <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>{e.key}</Typography>
        </Paper>
        <Typography sx={{
          fontWeight: 700, color: BLUE, fontFamily: 'Kanit, sans-serif', fontSize: 12,
          textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.4,
        }}>
          Cómo aplicarlo
        </Typography>
        <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>{e.how}</Typography>
      </Box>
    </Paper>
  );
}

const AntiAtraconesPage = () => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const handleUnlock = () => {
    if (hasValidVideoAccess()) {
      setUnlocked(true);
    } else {
      setShowLeadForm(true);
    }
  };

  const handleLeadSubmit = async (data) => {
    setShowLeadForm(false);
    setStoredVideoAccess(data);
    await submitLead({ data, source: 'anti-atracones' });
    setUnlocked(true);
  };

  return (
    <Box sx={{ bgcolor: GRAY_LIGHT, fontFamily: 'Kanit, sans-serif', minHeight: '100vh' }}>
      <AUHeader />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        {/* Hero */}
        <Typography sx={{
          fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: BLUE,
          textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif',
        }}>
          Material gratis
        </Typography>
        <Typography component="h1" sx={{
          fontFamily: 'Kanit, sans-serif', fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.8rem' }, color: DARK, mb: 1,
        }}>
          Guía Anti-Atracones
        </Typography>
        <Typography sx={{ color: GRAY, mb: 4, maxWidth: 680, fontFamily: 'Kanit, sans-serif' }}>
          El atracón no es falta de disciplina. Es un problema de estructura, y por eso no se arregla aguantando más.
          Estas son las 8 estrategias que uso con mis clientes para que la dieta se sostenga sin depender de la fuerza
          de voluntad.
        </Typography>

        {!unlocked ? (
          /* Gate — todo el material vive detrás del form */
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${BLUE}`, bgcolor: '#fff',
            p: { xs: 3, md: 5 }, textAlign: 'center', mt: 1,
          }}>
            <Typography sx={{ fontWeight: 800, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 1 }}>
              Desbloquea la guía completa
            </Typography>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', mb: 3, maxWidth: 560, mx: 'auto' }}>
              Déjanos tus datos y accede al instante a todo el material:
            </Typography>
            <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left', mb: 3.5 }}>
              {[
                'Las 8 estrategias completas, con el número clave de cada una',
                'La jerarquía nutricional: por qué la adherencia va antes que las calorías',
                'La estrategia más efectiva y la que casi nadie aplica',
                'El PDF descargable para tenerlo a mano',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                  <Box sx={{ color: BLUE, fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: 18, lineHeight: 1.35 }}>✓</Box>
                  <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained" disableElevation onClick={handleUnlock}
              sx={{
                py: 1.5, px: 5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 16,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Ver la guía completa
            </Button>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12.5, mt: 2 }}>
              Gratis. Solo te pedimos tus datos para enviártelo y acompañarte en el proceso.
            </Typography>
          </Paper>
        ) : (
        <>
        <DescargaPDF
          url={PDF_URL}
          titulo="Descarga la guía anti-atracones en PDF"
          bajada="Las 8 estrategias completas para tenerlas a mano. Abajo las tienes desarrolladas una por una."
        />
        {/* La tesis */}
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BLUE}`, bgcolor: BLUE_SOFT, mb: 5 }}>
          <Box sx={{ bgcolor: BLUE, px: { xs: 2, md: 3 }, py: 1.25 }}>
            <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: { xs: 15, md: 17 } }}>
              No es disciplina. Es estructura.
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 1.5 }}>
              Los atracones no son un problema de fuerza de voluntad. Con el sistema correcto, el entorno hace el
              trabajo que la voluntad no puede sostener sola. Ninguna de las 8 estrategias de abajo te pide aguantar.
            </Typography>
            <Typography sx={{ color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15 }}>
              Si vas a aplicar solo una, aplica la número 7.
            </Typography>
          </Box>
        </Paper>

        {/* Jerarquía nutricional */}
        <SectionTitle>Por qué estas 8 y no otras</SectionTitle>
        <Typography sx={{ color: GRAY, mb: 2.5, maxWidth: 760, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
          En coaching de físico hay una jerarquía que ordena toda recomendación nutricional. El nivel 1 no es contar
          calorías ni macros: es la adherencia, tu capacidad real de ejecutar el plan el tiempo necesario para que
          funcione.
        </Typography>
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 5 }}>
          {JERARQUIA.map((j, i) => (
            <Box key={j.n} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 2.5, py: 1.5, bgcolor: i % 2 === 0 ? BLUE_SOFT : '#fff',
            }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                bgcolor: j.destacado ? BLUE : GRAY, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 13,
              }}>
                {j.n}
              </Box>
              <Typography sx={{
                color: j.destacado ? BLUE_HOVER : DARK, fontFamily: 'Kanit, sans-serif',
                fontWeight: j.destacado ? 800 : 600, fontSize: 14.5, minWidth: 120,
              }}>
                {j.name}
              </Typography>
              <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>
                {j.desc}
              </Typography>
            </Box>
          ))}
        </Paper>

        {/* Las 8 estrategias */}
        <SectionTitle>Las 8 estrategias</SectionTitle>
        {ESTRATEGIAS.map((e) => <EstrategiaBlock key={e.n} e={e} />)}

        {/* PDF */}
        <SectionTitle>Llévatela contigo</SectionTitle>
        <Box sx={{ mb: 5 }}>
          <Button
            variant="contained" disableElevation component="a" href={PDF_URL} download
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Descargar PDF
          </Button>
        </Box>

        {/* CTA a coaching */}
        <Paper elevation={0} sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: DARK }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: { xs: 18, md: 22 }, mb: 0.75 }}>
            ¿Y si no tuvieras que pelear con esto solo?
          </Typography>
          <Typography sx={{ color: '#CBD5E1', fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 2.5, maxWidth: 640 }}>
            Un plan que se ajusta a tu semana real, con seguimiento cada siete días para corregir antes de que se
            caiga. Eso es exactamente lo que hacemos en AU Coaching.
          </Typography>
          <Button
            variant="contained" disableElevation onClick={() => navigate('/form')}
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Postula a AU Coaching
          </Button>
        </Paper>
        </>
        )}
      </Container>

      <VideoAccessForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        onSubmit={handleLeadSubmit}
        title="Desbloquea la guía anti-atracones"
        subtitle="Déjanos tus datos y te mostramos las 8 estrategias completas al instante, con el PDF para descargar."
        submitLabel="Ver la guía completa"
      />
    </Box>
  );
};

export default AntiAtraconesPage;
